/**
 * Currency formatting utilities.
 * All monetary amounts are stored internally as GBP pence and converted
 * to the user's chosen currency at display time using static exchange rates.
 */

/** Supported display currencies. */
export type Currency = 'GBP' | 'EUR' | 'USD';

/** Map of currency codes to their display symbols. */
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

/** Static exchange rates with GBP as the base currency. */
export const EXCHANGE_RATES: Record<Currency, number> = {
  GBP: 1,
  EUR: 1.17,
  USD: 1.27,
};

// Locale map for Intl.NumberFormat
const CURRENCY_LOCALES: Record<Currency, string> = {
  GBP: 'en-GB',
  EUR: 'de-DE',
  USD: 'en-US',
};

/**
 * Convert amount from GBP pence to target currency
 */
export const convertCurrency = (amountGbpPence: number, targetCurrency: Currency): number => {
  if (targetCurrency === 'GBP') return amountGbpPence;
  return Math.round(amountGbpPence * EXCHANGE_RATES[targetCurrency]);
};

/**
 * Format price in the specified currency
 * @param amountInPence - Amount in GBP pence (base currency)
 * @param currency - Target currency to display
 * @param options - Formatting options
 */
export const formatPrice = (
  amountInPence: number,
  currency: Currency = 'GBP',
  options?: {
    showDecimals?: boolean;
    convert?: boolean; // If true, converts from GBP to target currency
  }
): string => {
  const { showDecimals = true, convert = true } = options || {};

  // Convert to target currency if needed
  const convertedAmount = convert ? convertCurrency(amountInPence, currency) : amountInPence;
  const amount = convertedAmount / 100;

  return new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
    style: 'currency',
    currency,
    minimumFractionDigits: showDecimals ? 0 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(amount);
};

/**
 * Format price per token (smaller amounts need more precision)
 */
export const formatPricePerToken = (
  pricePerTokenGbpPence: number,
  currency: Currency = 'GBP'
): string => {
  const convertedAmount = convertCurrency(pricePerTokenGbpPence * 100, currency) / 100;
  const amount = convertedAmount / 100;

  return new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
    style: 'currency',
    currency,
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(amount);
};

/**
 * Get currency symbol
 */
export const getCurrencySymbol = (currency: Currency): string => {
  return CURRENCY_SYMBOLS[currency];
};

/**
 * Legacy currency formatter -- kept for backward compatibility.
 *
 * @param amountInPence - Amount in the smallest currency unit.
 * @param currency - ISO currency code.
 * @returns Formatted currency string.
 */
export const formatCurrency = (amountInPence: number, currency: string = 'GBP'): string => {
  const amount = amountInPence / 100;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a token count with locale-aware thousand separators.
 *
 * @param tokens - Number of tokens.
 * @returns Formatted token string (e.g. "1,500").
 */
export const formatTokens = (tokens: number): string => {
  return tokens.toLocaleString('en-GB');
};
